/**
 * The whole icon set: flat monochrome line drawings on one 24x24 grid.
 *
 * Every icon inherits colour from the text around it and sizes to 1em, so an
 * icon is styled by setting font-size and color on its parent — no per-icon CSS.
 */

import type { ReactNode } from "react";

import type { FoodCategory, StorageLocation } from "../features/food/types";

export type IconName =
  | "produce"
  | "meat"
  | "dairy"
  | "pantry"
  | "frozen"
  | "drinks"
  | "other"
  | "fridge"
  | "freezer"
  | "shelf"
  | "close"
  | "dot"
  | "alert"
  | "search"
  | "camera"
  | "spinner"
  | "edit"
  | "undo"
  | "recipes"
  | "settings"
  | "chevron"
  | "check"
  | "clock"
  | "plus"
  | "learn";

const SHAPES: Record<IconName, ReactNode> = {
  // A chef's knife on a board: the one object every technique here involves.
  learn: (
    <>
      <path d="M4 20h16" />
      <path d="M6 15.5 15.5 6" />
      <path d="M15.5 6c1.6-1.6 3.2-2.4 4-1.6.8.8 0 2.4-1.6 4l-2.8 2.8-2.4-2.4Z" />
    </>
  ),
  // Two strokes. Adding is not a concept that needs decoration.
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  // An apple with a leaf.
  produce: (
    <>
      <path d="M12 8.5c-3.6-2.6-7 .3-7 4 0 3.7 2.6 7.5 4.6 7.5 1 0 1.6-.6 2.4-.6s1.4.6 2.4.6c2 0 4.6-3.8 4.6-7.5 0-3.7-3.4-6.6-7-4Z" />
      <path d="M12 8.5V5.5" />
      <path d="M12 6c1.8 0 3-1.2 3-2.5-1.8 0-3 1.2-3 2.5Z" />
    </>
  ),
  // A cut of meat: irregular outline with the grain marked inside. A drumstick
  // was tried first and read as a magnifying glass at 16px.
  meat: (
    <>
      <path d="M4.7 13.4c-.8-3.6 2.2-7 6.7-7.6 4.5-.6 8.1 1.8 8.5 5.3.4 3.5-2.5 6.7-6.7 7.4-4.2.7-7.8-1.4-8.5-5.1Z" />
      <path d="M9.1 12.6c-.2-1.5 1-2.8 2.8-3 1.8-.2 3.3.8 3.5 2.3" />
    </>
  ),
  // A milk carton.
  dairy: (
    <>
      <path d="M7.5 9.5 12 5l4.5 4.5V19a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1Z" />
      <path d="M7.5 9.5h9" />
    </>
  ),
  // A lidded jar. Deliberately unlike the shelf icon below, which is the
  // pantry *place* rather than the pantry *kind of food*.
  pantry: (
    <>
      <rect x="8" y="3.5" width="8" height="3" rx="1" />
      <path d="M7.5 8.5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v9.5a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2Z" />
    </>
  ),
  // A snowflake.
  frozen: (
    <>
      <path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9" />
      <path d="M9.5 4.8 12 7l2.5-2.2M9.5 19.2 12 17l2.5 2.2" />
    </>
  ),
  // A glass with a straw.
  drinks: (
    <>
      <path d="M7.5 6h9l-1.2 13a1 1 0 0 1-1 .9h-4.6a1 1 0 0 1-1-.9Z" />
      <path d="M7.9 10.5h8.2" />
      <path d="M13.5 6l2-2.5" />
    </>
  ),
  other: (
    <>
      <circle cx="6.5" cy="12" r="1.1" />
      <circle cx="12" cy="12" r="1.1" />
      <circle cx="17.5" cy="12" r="1.1" />
    </>
  ),
  // Storage places share one silhouette so they read as a set.
  fridge: (
    <>
      <rect x="5.5" y="3" width="13" height="18" rx="2" />
      <path d="M5.5 10h13" />
      <path d="M8.5 6.5v1.5M8.5 12.5v2" />
    </>
  ),
  freezer: (
    <>
      <rect x="5.5" y="3" width="13" height="18" rx="2" />
      <path d="M12 8v8M9 9.5l6 5M15 9.5l-6 5" />
    </>
  ),
  shelf: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 9.5h16M4 15h16" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  /* Same line weight and 24-grid as the rest; a magnifier that isn't a circle
     and a stick reads as a different icon set. */
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5 20 20" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8.5a2 2 0 0 1 2-2h1.7l1.2-2h6.2l1.2 2H18a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
      <circle cx="12" cy="12.5" r="3.4" />
    </>
  ),
  /* A pan, for the recipes tab. */
  recipes: (
    <>
      <path d="M4.5 10.5h13v4.2a4 4 0 0 1-4 4h-5a4 4 0 0 1-4-4Z" />
      <path d="M17.5 11.5h1.6a1.9 1.9 0 0 1 0 3.8h-1.6" />
      <path d="M8 7.5c0-1 1-1.2 1-2.2M12 7.5c0-1 1-1.2 1-2.2" />
    </>
  ),
  /* Sliders rather than a gear: fewer strokes, reads better at 20px. */
  settings: (
    <>
      <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
      <circle cx="15" cy="8" r="2.1" />
      <circle cx="9" cy="16" r="2.1" />
    </>
  ),
  chevron: <path d="M8 10.5 12 14.5l4-4" />,
  check: <path d="M5 12.5 9.5 17 19 7.5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  /* An open ring: CSS spins it while the photo is being read. */
  spinner: <path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5" />,
  edit: (
    <>
      <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
      <path d="M14.5 6.5l3 3" />
    </>
  ),
  undo: (
    <>
      <path d="M4 9h10a5.5 5.5 0 0 1 0 11H8" />
      <path d="M7.5 5.5 4 9l3.5 3.5" />
    </>
  ),
  /* Signal marks. Text stays monochrome, so these carry the colour instead. */
  dot: <circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none" />,
  alert: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.8v4.6" />
      <circle cx="12" cy="15.9" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
};

/** Category and location ids are mapped to icons here, never guessed by name. */
export const CATEGORY_ICONS: Record<FoodCategory, IconName> = {
  produce: "produce",
  meat: "meat",
  dairy: "dairy",
  pantry: "pantry",
  frozen: "frozen",
  drinks: "drinks",
  other: "other",
};

export const LOCATION_ICONS: Record<StorageLocation, IconName> = {
  fridge: "fridge",
  freezer: "freezer",
  pantry: "shelf",
};

interface IconProps {
  name: IconName;
  className?: string;
  /**
   * Pass this only where the icon is the sole carrier of the meaning — an
   * inventory row, where the category has no text beside it. It names the icon
   * for screen readers and gives sighted users a hover tooltip. Leave it off
   * next to a visible label, where a second announcement is just noise.
   */
  label?: string;
  /**
   * Styling hooks like `data-open` or `data-tone`.
   *
   * Declared explicitly because TypeScript exempts hyphenated JSX attributes
   * from prop checking: passing `data-open` to a component that doesn't accept
   * it compiles cleanly and then does nothing. That silently broke the recipe
   * chevron's rotation until a test asked for the attribute back.
   */
  "data-open"?: boolean | undefined;
  "data-on"?: boolean | undefined;
  "data-tone"?: string | undefined;
  "data-category"?: string | undefined;
}

export function Icon({ name, className, label, ...data }: IconProps) {
  return (
    <svg
      {...data}
      className={className}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {label && <title>{label}</title>}
      {SHAPES[name]}
    </svg>
  );
}
